/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { SearchResultsIterator } from "./SearchResultsIterator";
import { SearchWindowManager } from "./SearchWindowManager";
import { SearchUIManager } from "./SearchUIManager";
import {
  searchStates,
  searchDirections,
  CURRENT_SEARCH_RESULT_COLOR,
  DEFAULT_SEARCH_RESULT_COLOR
} from "./SearchConstants";
import docApi from "api";
import { SEARCH_HIGH_PRIORITY, SEARCH_LOW_PRIORITY } from "constants/constants";
import { flatten, isUndefined, isEmpty, forEach, inRange } from "utils/lodashUtils";
import { getTotalPages, isSinglePageMode, isFullScreenOrFitOnePageMode } from "selectors/documentSelectors";
import { pagesInView } from "selectors/pagedViewSelectors";
import { getYPosition, getPagesViewYPos, getPagesViewXPos } from "selectors/pagedViewState";
import { getCanvasOperation } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";
import { getPageZoomLevel } from "selectors/zoom";
import {
  getViewPortHeightWithoutPagesViewScrollbar,
  getViewPortWidthWithoutPagesViewScrollbar,
  getDocumentVisibleHeight
} from "selectors/documentViewSelectors";
import CanvasQuad from "dc-preview-utils/src/stores/CanvasQuad";
import { SearchResults } from "./SearchResults";
import { getPageQuadsArray } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { canvasOperation } from "dc-preview-utils/src/Constants/enums";
import * as stores from "stores";
import { when } from "mobx";
import { getSearchState, getFirstSearchResultPromiseObj } from "selectors/searchSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { trackSearchExecuted } from "analytics/events";
import SearchManager from "./SearchManager";
import { getCoordinatesRelativeToTargetElem } from "utils/EventHandlingUtils";
import { mouseButtons, findPressedMouseButton } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { contextMenuManager } from "contextMenuManager";
import { pageContentHandler } from "pageContentHandler";
import { triggerSearchEvent, triggerSearchClearEvent } from "publicApis/Events/events";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import { transformQuadFromPDFToView, transformViewToRotatedView } from "dc-preview-utils/src/Utils/PageUtils";
import { QUAD_TYPE } from "dc-preview-utils/src/Constants/enums";

class ClassicPDFSearchManager extends SearchManager {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.searchUIManager = new SearchUIManager();
    this.state = searchStates.NO_ACTIVE_SEARCH;
    this.reset();
  }

  clearSearch() {
    this.clearAllPageCanvasQuads();
    this.reset();
    triggerSearchClearEvent();
  }

  reset(
    cachedSearchResults = null,
    searchWindowManager = null,
    queryString = "",
    searchDirection = searchDirections.NEXT,
    searchState = searchStates.NO_ACTIVE_SEARCH,
    searchResultsIterator = null
  ) {
    this.cachedSearchResults = cachedSearchResults;
    this.searchResultsIterator = searchResultsIterator;
    this.searchUIManager.resetUIState(stores);
    this.searchWindowManager = searchWindowManager;
    super.setQueryString(queryString);
    this.searchDirection = searchDirection;
    this.setState(searchState);
  }

  _clearFindResults() {
    if (getCanvasOperation(stores) === canvasOperation.CANVAS_OP_FIND) {
      this.clearSearch();
    }
  }

  handleMouseDownOnDocument() {
    this._clearFindResults();
  }

  handleTouchPress() {
    this._clearFindResults();
  }

  handleMouseDown(evt, pageIndex) {
    if (getCanvasOperation(stores) === canvasOperation.CANVAS_OP_FIND) {
      if (findPressedMouseButton(evt) === mouseButtons.RIGHT) {
        // this may be a ctx menu click over a searchResult
        const { x, y } = getCoordinatesRelativeToTargetElem(evt);
        this.clearSearch();
      } else {
        this.clearSearch();
      }
    }
  }

  closeSearchUI(event, triggerFrom) {
    let currSearchResult = null;
    if (this.searchResultsIterator !== null) {
      currSearchResult = this.searchResultsIterator.getCurrent().searchResult;
    }
    this.clearSearch();
    if (currSearchResult !== null) {
      currSearchResult.quads.forEach(quad => {
        quad.updateColor(DEFAULT_SEARCH_RESULT_COLOR);
        quad.updateType(QUAD_TYPE.DEF_SEARCH_RESULT);
      });
      this.updatePageCanvasQuads(currSearchResult.pageIndex, currSearchResult.quads, true);
    }
  }

  search(queryString, searchDirection) {
    const trimmedQueryString = queryString.trim();
    if (trimmedQueryString === this.queryString && this.state !== searchStates.SEARCHING_FOR_FIRST_SEARCH_RESULT) {
      if (searchDirection !== this.searchDirection) {
        this.searchDirection = searchDirection;
      }
      this.goToNewResult();
    } else if (trimmedQueryString !== this.queryString) {
      this.startNewSearch(trimmedQueryString, searchDirection);
    }
  }

  onNextClicked(queryString) {
    this.search(queryString, searchDirections.NEXT);
    stores.searchStore.sendOnResultsUpdate(stores);
  }

  onPreviousClicked(queryString) {
    this.search(queryString, searchDirections.PREVIOUS);
    stores.searchStore.sendOnResultsUpdate(stores);
  }

  startNewSearch(queryString, searchDirection) {
    if (getCanvasOperation(stores) !== canvasOperation.CANVAS_OP_FIND) {
      stores.pageHighlightsStore.setCanvasOperation(canvasOperation.CANVAS_OP_FIND);
    }
    this.clearAllPageCanvasQuads();
    this.reset(
      new SearchResults(),
      new SearchWindowManager(getTotalPages(stores), getCurrentPageIndex(stores)),
      queryString,
      searchDirection,
      searchStates.SEARCHING_FOR_FIRST_SEARCH_RESULT
    );
    this.goToNewResult();
    trackSearchExecuted();
    triggerSearchEvent(queryString);
  }

  resetIterator() {
    this.searchResultsIterator = new SearchResultsIterator(this.searchResultsIterator.getCurrent());
  }

  goToNewResult() {
    if (!this.cachedSearchResults) {
      return;
    }

    if (this.cachedSearchResults.isEmpty())
      if (this.getState() === searchStates.SEARCH_COMPLETE) {
        // do something here
      } else {
        this.searchForFirstSearchResult();
      }
    else {
      const newSearchResult = this.getNewSearchResultsFromCache();
      this.goToLocation(newSearchResult);
    }
  }

  getNewSearchResultsFromCache() {
    if (this.searchDirection === searchDirections.PREVIOUS) {
      return this.getPreviousSearchResultFromCache();
    }
    return this.getNextSearchResultFromCache();
  }

  getPreviousSearchResultFromCache() {
    return this.searchResultsIterator.getPrevious().searchResult;
  }

  getNextSearchResultFromCache() {
    return this.searchResultsIterator.getNext().searchResult;
  }

  onFirstSearchResultFound(quadsData) {
    this.processDocAPISearchResults(quadsData);
    this.searchUIManager.setProgressBarParameters(stores, 0, false);
    this.searchResultsIterator = new SearchResultsIterator(this.cachedSearchResults.getHead());
    this.goToLocation(this.searchResultsIterator.getCurrent().searchResult);
    this.startBackgroundSearch();
    subscribeToSelector(stores, pagesInView, this.searchInPagesInView.bind(this));
    if (getFirstSearchResultPromiseObj(stores) && !getFirstSearchResultPromiseObj(stores).done)
      getFirstSearchResultPromiseObj(stores).resolve();
  }

  startBackgroundSearch() {
    this.setState(searchStates.SEARCHING_IN_BACKGROUND);
    this.searchInBackground();
  }

  onNoSearchResultsFound() {
    this.searchUIManager.setProgressBarParameters(stores, 0, false);
    this.setState(searchStates.SEARCH_COMPLETE);
    this.searchUIManager.setNumberOfSearchResultsParameters(stores, true, 0);
    if (getFirstSearchResultPromiseObj(stores) && !getFirstSearchResultPromiseObj(stores).done)
      getFirstSearchResultPromiseObj(stores).reject();
  }

  searchForFirstSearchResult() {
    if (this.searchWindowManager && this.state === searchStates.SEARCHING_FOR_FIRST_SEARCH_RESULT) {
      let { startPage, searchWindowSize } = this.searchWindowManager.getNewSearchWindow(this.searchDirection);
      if (searchWindowSize > 0) {
        const pageToBeShownInProgressBar = this.searchWindowManager.getNumberOfSearchedPages() + 1;
        this.searchUIManager.setProgressBarParameters(stores, pageToBeShownInProgressBar, true);
        this.searchWindowManager.setPagesBeingSearchedState(startPage, searchWindowSize);
        const startPageNum = startPage + 1;
        let searchTextForCurrentQuery = this.queryString;
        return this.getDocAPIPromise(startPageNum, searchWindowSize, SEARCH_HIGH_PRIORITY)
          .then(response => {
            if (this.isDocAPIResponseForCurrentQuery(response, searchTextForCurrentQuery)) {
              this.searchWindowManager.setPagesSearchedState(startPage, searchWindowSize);
              if (this.receivedSearchResultsFromDocAPI(response, searchTextForCurrentQuery)) {
                this.onFirstSearchResultFound(response.data);
              } else {
                this.searchForFirstSearchResult();
              }
            }
          })
          .catch(error => {
            // console.log("error in searchForFirstSearchResult");
            this.searchForFirstSearchResult();
          });
      }
      this.onNoSearchResultsFound();
    }
  }

  searchInPagesInView(pagesInView) {
    if (this.searchWindowManager && this.isSearchInProgress()) {
      let { startPage, searchWindowSize } = this.searchWindowManager.getPagesNotSearchedFromWindow(
        pagesInView.start,
        pagesInView.end - pagesInView.start + 1
      );
      if (searchWindowSize > 0) {
        let searchTextForCurrentQuery = this.queryString;
        this.getDocAPIPromise(startPage + 1, searchWindowSize, SEARCH_HIGH_PRIORITY)
          .then(response => {
            if (this.receivedSearchResultsFromDocAPI(response, searchTextForCurrentQuery)) {
              let searchResultsOnPages = this.getPageSearchResultsFromQuadsData(response.data);
              searchResultsOnPages.forEach(searchResultsOnPage => {
                this.updatePageCanvasQuads(searchResultsOnPage.pageIndex, flatten(searchResultsOnPage.searchResults));
              });
            }
          })
          .catch(error => {
            console.log("error in searchInPagesInView");
          });
      }
    }
  }

  isSearchInProgress() {
    let result = false;
    if (
      this.state === searchStates.SEARCHING_FOR_FIRST_SEARCH_RESULT ||
      this.state === searchStates.SEARCHING_IN_BACKGROUND
    ) {
      result = true;
    }
    return result;
  }

  searchInBackground() {
    if (this.searchWindowManager && this.state === searchStates.SEARCHING_IN_BACKGROUND) {
      let { startPage, searchWindowSize } = this.searchWindowManager.getNewSearchWindow(searchDirections.NEXT);
      if (searchWindowSize > 0) {
        this.searchWindowManager.setPagesBeingSearchedState(startPage, searchWindowSize);
        let searchTextForCurrentQuery = this.queryString;
        this.getDocAPIPromise(startPage + 1, searchWindowSize, SEARCH_LOW_PRIORITY)
          .then(response => {
            if (this.isDocAPIResponseForCurrentQuery(response, searchTextForCurrentQuery)) {
              this.searchWindowManager.setPagesSearchedState(startPage, searchWindowSize);
              if (this.receivedSearchResultsFromDocAPI(response, searchTextForCurrentQuery)) {
                this.processDocAPISearchResults(response.data);
                stores.searchStore.sendOnResultsUpdate(stores);
              }
              this.searchInBackground();
            }
          })
          .catch(error => {
            // console.log("error in searchInBackground");
            this.searchInBackground();
          });
      } else {
        this.setState(searchStates.SEARCH_COMPLETE);
        stores.searchStore.sendOnResultsUpdate(stores);
      }
    }
  }

  isDocAPIResponseForCurrentQuery(response, stringToSearch) {
    let result = false;
    if (
      !isUndefined(response) &&
      !isUndefined(response.data) &&
      !isUndefined(stringToSearch) &&
      stringToSearch === this.queryString
    ) {
      result = true;
    }
    return result;
  }

  receivedSearchResultsFromDocAPI(response, stringToSearch) {
    if (this.isDocAPIResponseForCurrentQuery(response, stringToSearch) && !isEmpty(response.data)) {
      return true;
    }
    return false;
  }

  getDocAPIPromise(startPageNum, searchWindowSize, priority) {
    return docApi.performSearch(this.queryString, startPageNum, searchWindowSize, priority);
  }

  getState() {
    return this.state;
  }

  getQueryString() {
    return this.queryString;
  }

  setState(state) {
    this.state = state;
    if (stores.searchStore) {
      stores.searchStore.setSearchState(this.state);
    }
  }

  // the quads recieved are in PDF coordinates.
  getPageSearchResultsFromQuadsData(quadsData) {
    let searchResultsOnPages = [];
    forEach(quadsData, quadData => {
      const { quads, pageNum } = quadData;
      const numOfQuads = quads.length;
      const pageIndex = pageNum - 1;
      let searchResultsOnPage = searchResultsOnPages.find(
        searchResultsOnPage => searchResultsOnPage.pageIndex === pageIndex
      );
      if (isUndefined(searchResultsOnPage)) {
        searchResultsOnPage = { pageIndex, searchResults: [] };
        searchResultsOnPages.push(searchResultsOnPage);
      }
      let transformedQuads = [];
      for (let i = 0; i < numOfQuads; i++) {
        const currentQuad = quads[i];
        const transformedQuadPoints = transformQuadFromPDFToView(currentQuad, pageIndex, stores, 1);
        transformedQuads.push(
          new CanvasQuad({
            quadPoints: transformedQuadPoints,
            color: DEFAULT_SEARCH_RESULT_COLOR,
            type: QUAD_TYPE.DEF_SEARCH_RESULT
          })
        );
      }
      searchResultsOnPage.searchResults.push(transformedQuads);
    });
    searchResultsOnPages.sort((searchResultsOnPage1, searchResultsOnPage2) => {
      if (searchResultsOnPage1.pageIndex < searchResultsOnPage2.pageIndex) {
        return -1;
      }
      return 1;
    });
    return searchResultsOnPages;
  }

  processDocAPISearchResults(quadsData) {
    let searchResultsOnPages = this.getPageSearchResultsFromQuadsData(quadsData);
    searchResultsOnPages.forEach(searchResultsOnPage => {
      this.updatePageCanvasQuads(searchResultsOnPage.pageIndex, flatten(searchResultsOnPage.searchResults));
    });
    this.cachedSearchResults.mergeNewSearchResults(searchResultsOnPages, this.searchDirection);
    this.searchUIManager.setNumberOfSearchResultsParameters(stores, true, this.cachedSearchResults.length);
  }

  clearAllPageCanvasQuads() {
    if (getCanvasOperation(stores) === canvasOperation.CANVAS_OP_FIND) {
      pageContentHandler.getTextManagerInstance().clearSelection(stores);
      contextMenuManager.clearMenuItems(stores);
      stores.pageHighlightsStore.clearQuadsForPages(0);
    }
  }

  updatePageCanvasQuads(pageIndex, quads, replace = true) {
    stores.pageHighlightsStore.updatePageCanvasQuads(pageIndex, quads, replace);
  }

  revertColorOfPrecedingSearchResult() {
    let precedingSearchResult = null;
    if (this.searchDirection === searchDirections.PREVIOUS) {
      precedingSearchResult = this.searchResultsIterator.getCurrent().next.searchResult;
    } else {
      precedingSearchResult = this.searchResultsIterator.getCurrent().previous.searchResult;
    }
    if (precedingSearchResult !== null) {
      const { pageIndex, quads: searchResultQuads } = precedingSearchResult;
      const quadsOnPage = getPageQuadsArray(stores, pageIndex).slice();
      searchResultQuads.forEach(searchResultQuad => {
        const searchResultQuadOnPage = quadsOnPage.find(pageQuad => pageQuad.equals(searchResultQuad));
        if (searchResultQuadOnPage) {
          searchResultQuadOnPage.updateColor(DEFAULT_SEARCH_RESULT_COLOR);
          searchResultQuadOnPage.updateType(QUAD_TYPE.DEF_SEARCH_RESULT);
        }
      });
      this.updatePageCanvasQuads(pageIndex, quadsOnPage, true);
    }
  }

  goToLocation(searchResult) {
    this.revertColorOfPrecedingSearchResult();
    const { pageIndex, quads: searchResultQuads } = searchResult;
    const quadsOnPage = getPageQuadsArray(stores, pageIndex).slice();
    searchResultQuads.forEach(searchResultQuad => {
      const searchResultQuadOnPage = quadsOnPage.find(pageQuad => pageQuad.equals(searchResultQuad));
      if (searchResultQuadOnPage) {
        searchResultQuadOnPage.updateColor(CURRENT_SEARCH_RESULT_COLOR);
        searchResultQuadOnPage.updateType(QUAD_TYPE.CURR_SEARCH_RESULT);
      }
    });
    this.updatePageCanvasQuads(pageIndex, quadsOnPage, true);
    const firstQuad = searchResultQuads[0];
    const pageZoomLevel = getPageZoomLevel(stores, pageIndex);
    let newYPositionOfRectTop = getYPosition(stores, pageIndex, firstQuad.top);
    let viewPortHeight = getViewPortHeightWithoutPagesViewScrollbar(stores);
    if (getIsInPlaceMode(stores)) {
      viewPortHeight = getDocumentVisibleHeight(stores);
    }

    const viewPortWidth = getViewPortWidthWithoutPagesViewScrollbar(stores);
    let newXPositionOfRectLeft = firstQuad.left * pageZoomLevel;

    if (isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) {
      stores.pagedViewState.goToLocation(pageIndex);
    } else if (!this.isSearchResultVisible(newYPositionOfRectTop, newXPositionOfRectLeft)) {
      let newXPosition = firstQuad.left * pageZoomLevel - 0.2 * viewPortWidth;
      if (newXPosition < 0) {
        newXPosition = 0;
      }
      newYPositionOfRectTop -= viewPortHeight * 0.3;
      if (newYPositionOfRectTop < 0) {
        newYPositionOfRectTop = 0;
      }
      stores.pagedViewState.scrollPagesView(Math.ceil(newXPosition), Math.ceil(newYPositionOfRectTop));
    }
    this.searchUIManager.setIndexOfCurrentSearchResult(
      stores,
      this.searchResultsIterator.getCurrent().searchResult.searchResultIndex
    );
  }

  isSearchResultVisible(newYPositionOfRectTop, newXPositionOfRectLeft) {
    let viewPortHeight = getViewPortHeightWithoutPagesViewScrollbar(stores);
    if (getIsInPlaceMode(stores)) {
      viewPortHeight = getDocumentVisibleHeight(stores);
    }
    const pagesInViewYPos = getPagesViewYPos(stores);
    if (
      !inRange(newYPositionOfRectTop, pagesInViewYPos + 0.1 * viewPortHeight, pagesInViewYPos + 0.8 * viewPortHeight)
    ) {
      return false;
    }

    const viewPortWidth = getViewPortWidthWithoutPagesViewScrollbar(stores);
    const pagesInViewXPos = getPagesViewXPos(stores);
    if (!inRange(newXPositionOfRectLeft, pagesInViewXPos, pagesInViewXPos + viewPortWidth)) {
      return false;
    }

    return true;
  }

  getAllSearchResults() {
    return new Promise((resolve, reject) => {
      when(
        () => getSearchState(stores) === searchStates.SEARCH_COMPLETE,
        () => {
          let searchResultsArr = [];
          const getShallowInstance = obj => Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);
          this.cachedSearchResults.forEach(origResult => {
            const result = getShallowInstance(origResult);
            const { pageIndex, quads: searchResultQuads } = result.searchResult;
            const quadsOnPage = getPageQuadsArray(stores, pageIndex).slice();
            result.searchResult.quads = searchResultQuads.map(searchResultQuad => {
              const clonedSearchResultQuad = getShallowInstance(searchResultQuad);
              const searchResultQuadOnPage = quadsOnPage.find(pageQuad => pageQuad.equals(searchResultQuad));
              if (searchResultQuadOnPage) {
                clonedSearchResultQuad.updateColor(getShallowInstance(searchResultQuadOnPage.color));
              }
              return clonedSearchResultQuad;
            });
            searchResultsArr.push(result.searchResult);
          });

          resolve(searchResultsArr);
        }
      );
    });
  }

  getCurrentSearchResult() {
    if (this.searchResultsIterator && this.searchResultsIterator.getCurrent()) {
      return this.searchResultsIterator.getCurrent().searchResult;
    }

    return null;
  }

  rotateSearchResults(rotationAngle) {
    if (this.cachedSearchResults) {
      this.cachedSearchResults.forEach(cachedSearchResult => {
        const { searchResult } = cachedSearchResult;
        const pageIndex = searchResult.getPageIndex();
        const rotateFn = (x, y) => transformViewToRotatedView(x, y, pageIndex, stores, 1, rotationAngle);
        const rotatedQuads = searchResult.getQuads().map(quad => quad.transform(rotateFn));
        searchResult.updateQuads(rotatedQuads);
      });
    }
  }
}

export const classicPDFSearchManager = new ClassicPDFSearchManager();
